import { ContentType } from '@/@types/common'
import type { Post as PostDataType } from '@/@types/post'
import LocationFilledIcon from '@/assets/location-filled.svg?react'
import LockIcon from '@/assets/lock.svg?react'
import StickStickIcon from '@/assets/stick-stick.svg?react'
import { formatAsRelativeTime } from '@/utils/day'
import usePostInViewTracker from '@/utils/hooks/usePostInViewTracker'
import usePostNavigator from '@/utils/hooks/usePostNavigator'
import { toRoutePathParam } from '@/utils/route'
import { Link, useMatchRoute } from '@tanstack/react-router'
import clsx from 'clsx'
import { isEmpty } from 'lodash-es'
import { type ComponentProps, HTMLAttributes, useRef } from 'react'
import Avatar from '../Avatar'
import ScreenNames from '../ScreenNames'
import Tag from '../Tag'
import Topic from '../Topic'
import PostActions from './Actions'
import styles from './PostCard.module.css'
import PostContent from './PostContent'
import PostHeader from './PostHeader'
import PostTarget from './PostTarget'

export interface PostCardProps extends HTMLAttributes<HTMLDivElement> {
	mode?: 'simple' | 'full'
	data: PostDataType

	/**
	 * 禁用操作
	 *
	 * @default false
	 */
	noActions?: boolean

	noTopic?: boolean

	/**
	 * 显示对话线
	 *
	 * @default false
	 */
	showThreadLine?: boolean
}

export default function PostCard({
	mode = 'simple',
	data,
	noActions = false,
	showThreadLine = false,
	noTopic = false,
	className,
	...props
}: PostCardProps) {
	const { createdAt, topic, type, content, user, pinned, poi } = data
	const ref = useRef<HTMLDivElement>(null)
	const matchRoute = useMatchRoute()
	const isPostPage = !!matchRoute({
		to: '/u/$username/$type/$id',
	})

	const routeTypeParam = toRoutePathParam(data.type)
	const navigatable =
		!window.getSelection()?.toString().length &&
		[ContentType.POST, ContentType.REPOST].includes(data.type) &&
		!isEmpty(user) &&
		routeTypeParam &&
		!isPostPage

	const navigateToPost = usePostNavigator()

	const renderLeftSection = () => {
		if (!user) {
			return null
		}
		return (
			<div className={styles.leftSection}>
				<Avatar data={user} className={styles.avatar} />
				{showThreadLine && (
					<div className={styles.threadLineContainer}>
						<div className={styles.threadLine} />
					</div>
				)}
			</div>
		)
	}

	const renderHeader = () => {
		if (!user) {
			return null
		}
		return (
			<PostHeader
				title={
					<div className={styles.titleContainer}>
						<ScreenNames data={[user]} />
						{type === ContentType.REPOST && isEmpty(content) && (
							<span className={styles.shareLabel}>分享了</span>
						)}
					</div>
				}
				desc={
					<div className={styles.descContainer}>
						{formatAsRelativeTime(createdAt)}
						{poi && (
							<div className={styles.locationContainer}>
								<LocationFilledIcon className={styles.locationIcon} />
								<span>{poi.name}</span>
							</div>
						)}
					</div>
				}
				right={
					<>
						{data.isPrivate && (
							<Tag>
								<LockIcon />
								<span>仅自己可见</span>
							</Tag>
						)}
						{pinned?.personalUpdate && (
							<div className={styles.pinnedLabel}>
								<StickStickIcon className={styles.pinnedIcon} color="currentColor" />
								置顶
							</div>
						)}
					</>
				}
				className={styles.header}
			/>
		)
	}

	const renderContent = () => {
		if (!user) {
			return null
		}
		return (
			<div className={styles.contentWrapper}>
				<PostContent data={data} mode={mode} />
				{!noTopic && (
					<Topic
						data={topic}
						as={Link}
						to="/topic/$id/"
						onClick={(e) => e.stopPropagation()}
						params={{ id: topic?.id } as unknown as ComponentProps<typeof Link>['params']}
					/>
				)}
				<PostTarget data={data} />
			</div>
		)
	}

	usePostInViewTracker(data, ref)

	if (!user) {
		return null
	}

	return (
		<div
			ref={ref}
			onClick={(e) => {
				if (!navigatable) {
					return
				}
				navigateToPost(data, e)
			}}
			data-clickable-feedback={navigatable}
			className={clsx(styles.root, className)}
			{...props}
		>
			<div className={styles.container}>
				{renderLeftSection()}

				<div className={styles.content}>
					{renderHeader()}

					{renderContent()}

					{!noActions && <PostActions data={data} className={styles.actions} />}
				</div>
			</div>
		</div>
	)
}
